import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import { API, usePageTitle, useShop } from "@/context/ShopContext";

export default function CheckoutResult() {
  usePageTitle("Payment Status");
  const { clearCart } = useShop();
  const [state, setState] = useState("checking");
  const [orderNumber] = useState(() => localStorage.getItem("lamsah_pending_order"));
  const [pendingEmail] = useState(() => localStorage.getItem("lamsah_pending_email") || "");

  useEffect(() => {
    if (!orderNumber) {
      setState("unknown");
      return;
    }
    let tries = 0;
    let cancelled = false;
    const poll = async () => {
      tries += 1;
      try {
        const r = await fetch(`${API}/orders/${orderNumber}/payment-status?email=${encodeURIComponent(pendingEmail)}`);
        const d = await r.json();
        if (cancelled) return;
        if (d.payment_status === "paid") {
          setState("paid");
          clearCart();
          localStorage.removeItem("lamsah_pending_order");
          localStorage.removeItem("lamsah_pending_email");
          return;
        }
        if (d.payment_status === "failed") {
          setState("failed");
          return;
        }
      } catch (err) {
        console.warn("Payment status poll failed, retrying:", err);
      }
      if (tries < 15) setTimeout(poll, 2000);
      else setState("unknown");
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-4 py-24 md:py-32 text-center" data-testid="checkout-result-page">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="w-10 h-10 animate-spin text-taupe mx-auto" strokeWidth={1.5} />
          <h1 className="font-serif text-2xl sm:text-3xl font-light mt-8">Confirming your payment…</h1>
          <p className="text-sm text-espresso/70 mt-3">One moment — we're verifying with the bank.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-success">
          <span className="w-16 h-16 rounded-full bg-sage flex items-center justify-center mx-auto">
            <Check className="w-7 h-7 text-espresso" strokeWidth={1.5} />
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl font-light mt-8">Payment Received — Thank You</h1>
          <p className="text-sm text-espresso/70 mt-4 leading-relaxed">
            Your order <span className="font-medium text-espresso" data-testid="paid-order-number">{orderNumber}</span> is confirmed and paid.
            A confirmation email is on its way to you.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Link to="/shop" className="btn-primary" data-testid="result-continue-shopping">Continue Shopping</Link>
            <Link to="/" className="btn-secondary" data-testid="result-back-home">Back Home</Link>
          </div>
        </div>
      )}
      {(state === "failed" || state === "unknown") && (
        <div data-testid="payment-failed">
          <span className="w-16 h-16 rounded-full border border-rose/50 flex items-center justify-center mx-auto">
            <X className="w-7 h-7 text-rose" strokeWidth={1.5} />
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl font-light mt-8">
            {state === "failed" ? "Payment Not Completed" : "We Couldn't Confirm Your Payment"}
          </h1>
          <p className="text-sm text-espresso/70 mt-4 leading-relaxed">
            {state === "failed"
              ? "Your payment didn't go through — no money was taken. Your bag is saved, so you can try again."
              : "If money was deducted, it will be confirmed shortly or refunded by the bank. You can also contact us with your order number."}
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Link to="/cart" className="btn-primary" data-testid="result-retry">Return to Bag</Link>
            <Link to="/contact" className="btn-secondary" data-testid="result-contact">Contact Us</Link>
          </div>
        </div>
      )}
    </div>
  );
}
